import IRecorder from "./IRecorder";
import {emitRecording_started} from "../eventBus/notes/notesEventBus";
import {lessonPlayerStore} from "../../redux/lesson/LessonPlayerStore";
import {logger} from "../log/logger";

export default class MediaRecorderRecorder implements IRecorder {
  #mediaRecorder: MediaRecorder | null = null;
  #audioChunks: Blob[] = [];
  #audioContext: AudioContext;
  #isInitialized: boolean = false;
  #recordingStartTimeInMs: number | null = null;

  initialize(audioContext: AudioContext): Promise<void> {
    return new Promise<void>((resolve, _reject) => {
      this.#audioContext = audioContext;
      this.#isInitialized = true;
      resolve();
    });
  }

  #ensureRecorderInitialized(): void {
    if (!this.#isInitialized) {
      throw new Error("MediaRecorderRecorder is not initialized");
    }
  }

  /*
  Note it seems like we might be able to do real time pitch detection using the Media Recorder if we call
  MediaRecorder.requestData(). When this is called all media data which has been captured since recording began or
  the last time a dataavailable event occurred is delivered
  https://developer.mozilla.org/en-US/docs/Web/API/MediaRecorder/dataavailable_event
  */
  startListening(mediaStream: MediaStream): void {
    this.#ensureRecorderInitialized();
    this.#reset();

    /*
    128kbps: A common bitrate, often considered good enough for casual listening, especially on devices with limited storage.
    192kbps: A good compromise between file size and quality, suitable for most listening scenarios.
    256kbps: A high-quality option that's a noticeable improvement over 192kbps, especially for music with complex instrumentation.
    320kbps: The highest quality supported by the MP3 standard, offering near-CD quality sound.
     */
    const mediaRecorderOptions = {
      // Using 128kbps cuz it's lower than the default value (256 kbps). This should help with speed and should be good
      // enough for our pitch detection needs. Might be able to go lower, but need to do more testing to be certain.
      audioBitsPerSecond: 128000,
    };

    this.#mediaRecorder = new MediaRecorder(mediaStream, mediaRecorderOptions);

    // Collect chunks of audio data as they are recorded
    this.#mediaRecorder.addEventListener("dataavailable", this.#onMoreMediaRecorderDataAvailable.bind(this));

    // Request data every 1000ms (1 second) to avoid browser buffer limits
    // Without a timeslice, the browser may drop audio data on long recordings
    const timesliceMs = 1000;
    this.#mediaRecorder.start(timesliceMs);

    this.#recordingStartTimeInMs = this.#audioContext.currentTime * 1000;

    logger.pitchDetection.info({
      message: "Recording started",
      data: [
        {key: "recordingStartTimeInMs", value: this.#recordingStartTimeInMs}
      ]
    });

    lessonPlayerStore.setRecordingStartTimeInMs(this.#recordingStartTimeInMs);

    emitRecording_started();
  }

  #onMoreMediaRecorderDataAvailable(event: BlobEvent): void {
    this.#audioChunks.push(event.data);
    logger.pitchDetection.info({
      message: "Audio chunk received",
      data: [
        {key: "chunk.size", value: event.data.size},
        {key: "totalChunks", value: this.#audioChunks.length}
      ]
    });
  }

  stopListening(): Promise<void> {
    this.#ensureRecorderInitialized();
    return new Promise<void>((resolve, _reject) => {
      if (this.#mediaRecorder) {
        this.#mediaRecorder.onstop = (_event) => {
          const stopTimeInMs = this.#audioContext.currentTime * 1000;

          logger.pitchDetection.info({
            message: "Recording stopped",
            data: [
              {key: "stopTimeInMs", value: stopTimeInMs},
              {key: "audioChunks.length", value: this.#audioChunks.length}
            ]
          });

          // Wait a bit to ensure the final dataavailable event has been processed
          // The final dataavailable event can fire after onstop in some browsers
          // ☝️This comment was written by AI. Without further research, its validity is in question.
          setTimeout(() => {
            logger.pitchDetection.info({
              message: "Recording stopped - after timeout",
              data: [
                {key: "audioChunks.length", value: this.#audioChunks.length}
              ]
            });
            
            // Cleanup after all data has been collected
            this.#mediaRecorder = null;
            resolve();
          }, 100);
        };
        
        this.#mediaRecorder.stop(); // calling stop here triggers the mediaRecorder onstop event
      } else {
        resolve();
      }
    });
  }

  async getAudioBuffer(): Promise<AudioBuffer> {
    this.#ensureRecorderInitialized();
    return new Promise((resolve, reject) => {
      const audioBlob = new Blob(this.#audioChunks, { type: 'audio/wav' });

      logger.pitchDetection.info({
        message: "getAudioBuffer - creating blob from chunks",
        data: [
          {key: "audioChunks.length", value: this.#audioChunks.length},
          {key: "audioBlob.size", value: audioBlob.size}
        ]
      });

      const reader = new FileReader();
      reader.readAsArrayBuffer(audioBlob);
      reader.onloadend = () => {
        const arrayBuffer = reader.result as ArrayBuffer;

        this.#audioContext.decodeAudioData(arrayBuffer, (audioBuffer: AudioBuffer) => {
          logger.pitchDetection.info({
            message: "getAudioBuffer - audio decoded",
            data: [
              {key: "audioBuffer.duration", value: audioBuffer.duration},
              {key: "audioBuffer.length", value: audioBuffer.length},
              {key: "audioBuffer.sampleRate", value: audioBuffer.sampleRate},
              {key: "audioBuffer.numberOfChannels", value: audioBuffer.numberOfChannels}
            ]
          });
          resolve(audioBuffer);
        }, (e: Error) => {
          reject(e);
          logger.pitchDetection.error({message: "Error decoding audio data", error: e});
        });
      }
    });
  }

  #reset(): void {
    this.#resetBuffer();
    this.#resetRecordingStartTime();
  }

  #resetBuffer(): void {
    this.#audioChunks = [];
  }

  #resetRecordingStartTime(): void {
    this.#recordingStartTimeInMs = null;
  }

}